import { useCallback, useEffect, useState } from 'react';
import usePersistedState from './usePersistedState';
import config from 'config';
import TagManager from 'react-gtm-module';
import { registrationActions } from 'careand-redux/slices/registration';
import { useDispatch } from 'react-redux';
import { api, updateTrackingHeaders } from 'careand-redux/api/ApiThunks';
import { useAppSelector } from 'careand-redux/store';
import { getActivePath } from 'careand-redux/slices/ui/navigation';
import ReactPixel from 'react-facebook-pixel';
import { getAnalyticsConfig } from 'services/portalData';

/** Tracking is on only when this tenant has a configured Matomo instance. */
const matomoEnabled = () => {
  const analytics = getAnalyticsConfig();
  return !!analytics?.url && !!analytics.site_id;
};

function extractClickId(cookieString, platform) {
  switch (platform.toLowerCase()) {
    case 'google': {
      const gclMatch = cookieString.match(/_gcl_aw=GCL\.\d+\.(\d+)/);
      return gclMatch ? gclMatch[1] : null;
    }
    case 'fbc': {
      const fbcMatch = cookieString.match(/_fbc=([^;]+)/);
      return fbcMatch ? fbcMatch[1] : null;
    }
    case 'fbp': {
      const fbpMatch = cookieString.match(/_fbp=([^;]+)/);
      return fbpMatch ? fbpMatch[1] : null;
    }
    default: {
      return null;
    }
  }
}

const DEFAULT_OBJECT = {};

const useAnalytics = () => {
  const dispatch = useDispatch();
  const activePath = useAppSelector((state) => getActivePath(state.navigation));
  const activeUserId = useAppSelector((state) => state.authentication.activeUserId);
  const patient = useAppSelector((state) => state.patients.byId[activeUserId]);
  const hasAdTrackingPermission = useAppSelector((state) => {
    const role = state.authentication.role;
    return !role || role === 'patient';
  });
  const [trackingIds, setTrackingIds] = usePersistedState('trackingIds', DEFAULT_OBJECT);

  const [matomoTrackId, setMatomoTrackId] = useState();
  const googleClickId = extractClickId(document.cookie, 'google');
  const metaClickId = extractClickId(document.cookie, 'fbc');
  const metaPixelId = extractClickId(document.cookie, 'fbp');

  useEffect(() => {
    updateTrackingHeaders(trackingIds);
  }, [trackingIds]);

  useEffect(() => {
    if (!patient || !hasAdTrackingPermission || !config.FACEBOOK_PIXEL) {
      return;
    }
    ReactPixel.init(
      config.FACEBOOK_PIXEL,
      {
        em: patient.email,
        external_id: patient.id,
      },
      {
        autoConfig: true,
        debug: config.ENVIRONMENT !== 'production',
      },
    );
  }, [patient?.id, patient?.email, hasAdTrackingPermission]);

  useEffect(() => {
    if (!patient || !hasAdTrackingPermission) {
      return;
    }
    dispatch(
      api.Patients.updatePatient({
        patient: {
          ...patient,
          meta_tracking: {
            pixelId: metaPixelId,
            clickId: metaClickId,
          },
        },
        id: patient.id,
      }),
    );

    //for now dependency array can only track patient.id to prevent infinite loop
  }, [metaClickId, metaPixelId, patient?.id, hasAdTrackingPermission]);

  useEffect(() => {
    setTrackingIds((prevState) => {
      return {
        ...prevState,
        gclid: googleClickId,
        matomo_id: matomoTrackId,
        fbc: metaClickId,
        fbp: metaPixelId,
      };
    });
  }, [googleClickId, matomoTrackId, metaClickId, metaPixelId]);

  useEffect(() => {
    initializeGoogleTagManager();
    initializeMatomo();
  }, [activeUserId]);

  const initializeGoogleTagManager = () => {
    if (config.GOOGLE_TAG_ID && hasAdTrackingPermission) {
      TagManager.initialize({ gtmId: config.GOOGLE_TAG_ID });
    }
  };

  useEffect(() => {
    getMatomoVisitorId().then((track_id) => {
      if (track_id) {
        setMatomoTrackId(track_id);
        dispatch(registrationActions.setTrackId(track_id));
      }
    });
  }, []);

  const initializeMatomo = () => {
    // The tenant's own Matomo instance, from portal-data (available pre-auth).
    // No analytics module configured => no tracking at all.
    const analytics = getAnalyticsConfig();
    if (analytics?.url && analytics.site_id) {
      const url = analytics.url;
      window._paq = window._paq || [];
      matomoPushInstruction('trackPageView');
      matomoPushInstruction('enableLinkTracking');

      const d = document;
      const g = d.createElement('script');
      const s = d.getElementsByTagName('script')[0];
      g.async = true;
      g.src = url + (analytics.script_path || 'matomo.js');
      s.parentNode.insertBefore(g, s);

      matomoPushInstruction('setTrackerUrl', url + (analytics.tracker_path || 'matomo.php'));
      matomoPushInstruction('setSiteId', analytics.site_id);
      if (analytics.domains?.length) {
        matomoPushInstruction('setDomains', analytics.domains);
        matomoPushInstruction('enableCrossDomainLinking');
      }
    }
  };

  const matomoPushInstruction = (eventName, ...vals) => {
    try {
      if (window._paq) {
        window._paq.push([eventName, ...vals]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getMatomoVisitorId = async () => {
    if (matomoEnabled()) {
      if (matomoTrackId) return matomoTrackId;
      if (window._paq) {
        return new Promise((resolve) => {
          window._paq.push([
            function () {
              resolve(this.getVisitorId());
            },
          ]);
        });
      }
    }
  };

  useEffect(() => {
    if (matomoEnabled() && !!patient && !patient.matomo_id) {
      getMatomoVisitorId().then((matomo_id) => {
        if (matomo_id) {
          dispatch(
            api.Patients.updatePatient({
              patient: { ...patient, matomo_id },
              id: patient.id,
            }),
          );
        }
      });
    }
  }, [dispatch, patient]);

  useEffect(() => {
    trackPageView(activePath, patient);
  }, [activePath, patient]);

  const trackPageView = (pathname, patient) => {
    if (hasAdTrackingPermission) {
      ReactPixel.pageView();
      trackTagManager('pageview', { pagePath: pathname });
    }
    trackMatomo(pathname, patient);
  };

  const trackMatomo = (pathname, patient) => {
    if (matomoEnabled()) {
      if (patient?.matomo_id) {
        matomoPushInstruction('setVisitorId', patient.matomo_id);
      }
      matomoPushInstruction('setCustomUrl', pathname);
      matomoPushInstruction('trackPageView');
    }
  };

  const trackTagManager = useCallback(
    (event, data = {}) => {
      if (!!config.GOOGLE_TAG_ID) {
        // Base data layer object
        const dataLayerObject = {
          event,
          user_data: patient
            ? {
                email_address: patient.email,
              }
            : undefined,
          ...data,
        };

        if (dataLayerObject.value) {
          dataLayerObject.currency = 'CAD';
        }

        TagManager.dataLayer({
          dataLayer: dataLayerObject,
        });
      }
    },
    [patient],
  );

  return {
    trackingIds,
    trackTagManager,
  };
};

export default useAnalytics;
